export type Role = 'owner' | 'admin' | 'manager' | 'cashier' | 'staff' | 'viewer' | 'individual' | 'ledger_member' | 'queue_member' | 'employee_member' | 'gym_member' | 'canteen_member' | 'pos_member';
export type PlanId = 'basic' | 'pro' | 'ultra';

export interface Permissions {
  // Navigation
  canAccessGenerator: boolean;
  canAccessInvoiceCreator: boolean;
  canAccessBatchGenerate: boolean;
  canAccessBills: boolean;
  canAccessAccounts: boolean;
  canAccessProducts: boolean;
  canAccessLogs: boolean;
  canAccessProfile: boolean;
  canAccessTeam: boolean;
  canAccessPOS: boolean;
  canAccessLedger: boolean;
  canAccessInventory: boolean;
  canAccessEmployees: boolean;
  // ── NEW: plan-gated view access ──────────────────────────────────
  canAccessQueue: boolean;
  canAccessClinic: boolean;
  canAccessFitness: boolean;
  canAccessFood: boolean;
  // Invoices
  canEditInvoice: boolean;
  canDeleteInvoice: boolean;
  canChangeInvoiceStatus: boolean;
  canExportPDF: boolean;
  canExportExcel: boolean;
  canShareInvoice: boolean;
  canEditAccounts: boolean;
 canDeleteAccounts: boolean;
 canSendPromo: boolean;
  // Products
  canAddProduct: boolean;
  canEditProduct: boolean;
  canRemoveProduct: boolean;
  canImportProducts: boolean;
  // POS
  canPOSApplyDiscount: boolean;
  canPOSOverridePrice: boolean;
  canPOSChangeStatus: boolean;
  canPOSOverrideInvNo: boolean;
  canPOSChangeBusinessType: boolean;
  canPOSEditDateTime: boolean;
  canPOSEditTaxLines: boolean;
  canPOSEditShipping: boolean;
  canPOSManageAutoActions: boolean;
  canPOSDeleteBill: boolean;
  canPOSDiscardBill: boolean;
  canAccessKitchenDashboard: boolean;
  canAccessPaymentDisplay: boolean;
  canPOSToggleBilingual: boolean;
  canEditTableLayout: boolean;
  canManageFloors: boolean;
  canAccessTables: boolean;

  // Ledger
  canLedgerCreateTab: boolean;
  canLedgerAddCharge: boolean;
  canLedgerAddPayment: boolean;
  canLedgerSettle: boolean;
  canLedgerDeleteEntry: boolean;
  canLedgerBroadcast: boolean;
  canLedgerSendWA: boolean;
  canLedgerExportExcel: boolean;
  canLedgerShareQR: boolean;
  canLedgerQuickMsg: boolean;
  canLedgerNotes: boolean;
  canLedgerRecurring: boolean;
  canLedgerPayLink: boolean;
  canLedgerEditTab: boolean;
  canLedgerDeleteTab: boolean;
  canLedgerViewStatus: boolean;
  canLedgerManageCategories: boolean;
  // Outlets
  canManageOutlets: boolean;
  canManageOutletStaff: boolean;
  canViewAllOutlets: boolean;
  canManageProfileMessaging?: boolean;
  canManageCurrency?: boolean;
  canManageGateways?: boolean;
  // Inventory
  canManageInventory: boolean;
  canManageSuppliers: boolean;
  // Employees
  canManageEmployees: boolean;
  canManagePayroll: boolean;
  canManageLeaves: boolean;
  canManageAttendance: boolean;
  canViewAttendance: boolean;
  canViewLeaves: boolean;
  canViewPayroll: boolean;
  canRequestLeave: boolean;
  // Queue
  canQueueIssueToken: boolean;
  canQueueCallNext: boolean;
  canQueueUpdateStatus: boolean;
  canQueueSendWhatsApp: boolean;
  canQueueSendEmail: boolean;
  canQueueEditToken: boolean;
  canQueueDeleteToken: boolean;
  canQueueManageCounters: boolean;
  canQueueSettings: boolean;
  canQueueViewLogs: boolean;
  // Gym
  canGymCheckInQR: boolean;
  canGymCheckInFace: boolean;
  canGymCheckInFinger: boolean;
  canGymAddMember: boolean;
  canGymEditMember: boolean;
  canGymDeleteMember: boolean;
  canGymViewBadge: boolean;
  canGymRegisterFace: boolean;
  canGymVerifyOtp: boolean;
  canGymSendReports: boolean;
  canGymSendPaymentLink: boolean;
  canGymViewStatus: boolean;
  canGymManagePlans: boolean;
  canGymManageKiosk: boolean;
  // Food / Canteen
  canFoodCheckInQR: boolean;
  canFoodCheckInFace: boolean;
  canFoodCheckInFinger: boolean;
  canFoodAddMember: boolean;
  canFoodEditMember: boolean;
  canFoodDeleteMember: boolean;
  canFoodViewBadge: boolean;
  canFoodRegisterFace: boolean;
  canFoodVerifyOtp: boolean;
  canFoodSendReports: boolean;
  canFoodSendPaymentLink: boolean;
  canFoodViewStatus: boolean;
  canFoodManagePlans: boolean;
  canFoodManageKiosk: boolean;
  // ── Plan-gated features ──────────────────────────────────────────
  canUseBilingual: boolean;
  canUseWhatsApp: boolean;
  canUseEmailReminder: boolean;
  canMergePDF: boolean;
  canUseDriveSync: boolean;
  canExportExcelBulk: boolean;
  canManageMultiOutlets: boolean;
  canUseSmartReconciliation: boolean;
  canUseFaceRecognition: boolean;
  canUseFingerprint: boolean;
  canUsePayroll: boolean;
  canUseScanner: boolean;
  canAddMultipleTaxLines: boolean;
  canAddDiscountLines: boolean;
  canUseShipping: boolean;
  canUseCustomColumns: boolean;
  canUseNotes: boolean;
}

// ── Plan gate matrix ──────────────────────────────────────────────────────────
// Basic: invoicing only (5 invoices/mo enforced backend)
// Pro: everything except Ultra-only features
// Ultra: everything
// NOTE: Ledger / Employees / Queue / Clinic / Fitness / Food are NO LONGER part of
// this matrix's job to grant. They are independent standalone module plans — see
// MODULE_GATES below. The Full Suite plan matrix only governs invoicing-side
// features (POS, bilingual, WhatsApp send, etc).

const PLAN_GATES: Record<PlanId, Partial<Permissions>> = {
  basic: {
    canAccessGenerator: true,
    canAccessInvoiceCreator: true,
    canAccessBatchGenerate: true,
    canAccessBills: true,
    canAccessAccounts: true,
    canAccessTeam: true,
    canAccessProducts: true,
    canUseBilingual: false,
    canUseWhatsApp: false,
    canUseEmailReminder: false,
    canMergePDF: false,
    canUseDriveSync: false,
    canExportExcelBulk: false,
    canManageMultiOutlets: false,
    canUseSmartReconciliation: false,
    canUseFaceRecognition: false,
    canUseFingerprint: false,
    canUseScanner: false,
    canAddMultipleTaxLines: true,
    canAddDiscountLines: true,
    canUseShipping: true,
    canUseCustomColumns: true,
    canUseNotes: true,
  },
  pro: {
    canAccessGenerator: true,
    canAccessInvoiceCreator: true,
    canAccessBatchGenerate: true,
    canAccessBills: true,
    canAccessAccounts: true,
    canAccessTeam: true,
    canAccessProducts: true,
    canUseBilingual: true,
    canUseWhatsApp: true,
    canUseEmailReminder: true,
    canMergePDF: true,
    canUseDriveSync: true,
    canExportExcelBulk: true,
    canManageMultiOutlets: true,
    canUseSmartReconciliation: false,   // Ultra only
    canUseFaceRecognition: false,       // Ultra only
    canUseFingerprint: true,
    canUseScanner: true,
    canAddMultipleTaxLines: true,
    canAddDiscountLines: true,
    canUseShipping: true,
    canUseCustomColumns: true,
    canUseNotes: true,
  },
  ultra: {
    canAccessGenerator: true,
    canAccessInvoiceCreator: true,
    canAccessBatchGenerate: true,
    canAccessBills: true,
    canAccessAccounts: true,
    canAccessTeam: true,
    canAccessProducts: true,
    canUseBilingual: true,
    canUseWhatsApp: true,
    canUseEmailReminder: true,
    canMergePDF: true,
    canUseDriveSync: true,
    canExportExcelBulk: true,
    canManageMultiOutlets: true,
    canUseSmartReconciliation: true,
    canUseFaceRecognition: true,
    canUseFingerprint: true,
    canUseScanner: true,
    canAddMultipleTaxLines: true,
    canAddDiscountLines: true,
    canUseShipping: true,
    canUseCustomColumns: true,
    canUseNotes: true,
  },
};

// ── Defaults (all false — module gates + plan gates layer on top) ─────────────

const PLAN_DEFAULTS: Pick<Permissions,
  'canAccessLedger' | 'canAccessEmployees' | 'canAccessQueue' | 'canAccessClinic' | 'canAccessFitness' | 'canAccessFood' |
  'canAccessGenerator' | 'canAccessInvoiceCreator' | 'canAccessBatchGenerate' | 'canAccessPOS' | 'canAccessInventory' | 'canAccessProducts' |
  'canUseBilingual' | 'canUseWhatsApp' | 'canUseEmailReminder' | 'canMergePDF' |
  'canUseDriveSync' | 'canExportExcelBulk' | 'canManageMultiOutlets' |
  'canUseSmartReconciliation' | 'canUseFaceRecognition' | 'canUseFingerprint' |
  'canUsePayroll' | 'canUseScanner' | 'canAddMultipleTaxLines' | 'canAddDiscountLines' |
  'canUseShipping' | 'canUseCustomColumns' | 'canUseNotes' |
  'canLedgerCreateTab' | 'canLedgerAddCharge' | 'canLedgerAddPayment' | 'canLedgerSettle' |
  'canLedgerDeleteEntry' | 'canLedgerBroadcast' |
  'canLedgerSendWA' | 'canLedgerExportExcel' | 'canLedgerShareQR' | 'canLedgerQuickMsg' |
  'canLedgerNotes' | 'canLedgerRecurring' | 'canLedgerPayLink' |
  'canLedgerEditTab' | 'canLedgerDeleteTab' | 'canLedgerViewStatus' | 'canLedgerManageCategories' |
  'canManageEmployees' | 'canManageLeaves' | 'canManageAttendance' |
  'canViewAttendance' | 'canViewLeaves' | 'canViewPayroll' | 'canRequestLeave' |
  'canQueueIssueToken' | 'canQueueCallNext' | 'canQueueUpdateStatus' |
  'canQueueSendWhatsApp' | 'canQueueSendEmail' | 'canQueueEditToken' | 'canQueueDeleteToken' |
  'canQueueManageCounters' | 'canQueueSettings' | 'canQueueViewLogs' |
  'canGymCheckInQR' | 'canGymCheckInFace' | 'canGymCheckInFinger' |
  'canGymAddMember' | 'canGymEditMember' | 'canGymDeleteMember' |
  'canGymViewBadge' | 'canGymRegisterFace' | 'canGymVerifyOtp' |
  'canGymSendReports' | 'canGymSendPaymentLink' | 'canGymViewStatus' |
  'canGymManagePlans' | 'canGymManageKiosk' |
  'canFoodCheckInQR' | 'canFoodCheckInFace' | 'canFoodCheckInFinger' |
  'canFoodAddMember' | 'canFoodEditMember' | 'canFoodDeleteMember' |
  'canFoodViewBadge' | 'canFoodRegisterFace' | 'canFoodVerifyOtp' |
  'canFoodSendReports' | 'canFoodSendPaymentLink' | 'canFoodViewStatus' |
  'canFoodManagePlans' | 'canFoodManageKiosk'
> = {
  canAccessLedger: false,
  canAccessEmployees: false,
  canAccessQueue: false,
  canAccessClinic: false,
  canAccessFitness: false,
  canAccessFood: false,
  canAccessGenerator: false,
  canAccessInvoiceCreator: false,
  canAccessBatchGenerate: false,
  canAccessPOS: false,
  canAccessInventory: false,
  canAccessProducts: false,
  canUseBilingual: false,
  canUseWhatsApp: false,
  canUseEmailReminder: false,
  canMergePDF: false,
  canUseDriveSync: false,
  canExportExcelBulk: false,
  canManageMultiOutlets: false,
  canUseSmartReconciliation: false,
  canUseFaceRecognition: false,
  canUseFingerprint: false,
  canUsePayroll: false,
  canUseScanner: false,
  canAddMultipleTaxLines: false,
  canAddDiscountLines: false,
  canUseShipping: false,
  canUseCustomColumns: false,
  canUseNotes: false,
  canLedgerCreateTab: false,
  canLedgerAddCharge: false,
  canLedgerAddPayment: false,
  canLedgerSettle: false,
  canLedgerDeleteEntry: false,
  canLedgerBroadcast: false,
  canLedgerSendWA: false,
  canLedgerExportExcel: false,
  canLedgerShareQR: false,
  canLedgerQuickMsg: false,
  canLedgerNotes: false,
  canLedgerRecurring: false,
  canLedgerPayLink: false,
  canLedgerEditTab: false,
  canLedgerDeleteTab: false,
  canLedgerViewStatus: false,
  canLedgerManageCategories: false,
  canManageEmployees: false,
  canManageLeaves: false,
  canManageAttendance: false,
  canViewAttendance: false,
  canViewLeaves: false,
  canViewPayroll: false,
  canRequestLeave: false,
  canQueueIssueToken: false,
  canQueueCallNext: false,
  canQueueUpdateStatus: false,
  canQueueSendWhatsApp: false,
  canQueueSendEmail: false,
  canQueueEditToken: false,
  canQueueDeleteToken: false,
  canQueueManageCounters: false,
  canQueueSettings: false,
  canQueueViewLogs: false,
  canGymCheckInQR: false,
  canGymCheckInFace: false,
  canGymCheckInFinger: false,
  canGymAddMember: false,
  canGymEditMember: false,
  canGymDeleteMember: false,
  canGymViewBadge: false,
  canGymRegisterFace: false,
  canGymVerifyOtp: false,
  canGymSendReports: false,
  canGymSendPaymentLink: false,
  canGymViewStatus: false,
  canGymManagePlans: false,
  canGymManageKiosk: false,
  canFoodCheckInQR: false,
  canFoodCheckInFace: false,
  canFoodCheckInFinger: false,
  canFoodAddMember: false,
  canFoodEditMember: false,
  canFoodDeleteMember: false,
  canFoodViewBadge: false,
  canFoodRegisterFace: false,
  canFoodVerifyOtp: false,
  canFoodSendReports: false,
  canFoodSendPaymentLink: false,
  canFoodViewStatus: false,
  canFoodManagePlans: false,
  canFoodManageKiosk: false,
};

export const ROLE_PERMISSIONS: Record<Role, Permissions> = {
  owner: {
    canAccessGenerator: false, canAccessInvoiceCreator: false, canAccessBatchGenerate: false, canAccessBills: false, canAccessAccounts: false,
    canAccessProducts: false, canAccessLogs: true, canAccessProfile: true,
    canAccessTeam: true, canAccessPOS: true,
    canAccessInventory: true,
    canEditInvoice: true, canDeleteInvoice: true,
    canExportPDF: true, canExportExcel: true, canShareInvoice: true,
    canAddProduct: true, canEditProduct: true, canRemoveProduct: true, canImportProducts: true,
    canPOSApplyDiscount: true, canPOSOverridePrice: true, canPOSChangeStatus: true, canPOSOverrideInvNo: true,
    canManageOutlets: true, canManageOutletStaff: true, canViewAllOutlets: true,canChangeInvoiceStatus: true,
    canManageInventory: true, canManageSuppliers: true,
    canManagePayroll: true, canPOSChangeBusinessType: true,canPOSEditDateTime: true, canPOSEditTaxLines: true, canPOSEditShipping: true,
 canPOSManageAutoActions: true, canPOSDeleteBill: true, canPOSDiscardBill: true,canEditAccounts: true, canDeleteAccounts: true, canSendPromo: true,
    canAccessKitchenDashboard: true, canAccessPaymentDisplay: true, canPOSToggleBilingual: true,
    canEditTableLayout: true, canManageFloors: true, canAccessTables: true,
    ...PLAN_DEFAULTS,
  },
  individual: {
    canAccessGenerator: false, canAccessInvoiceCreator: false, canAccessBatchGenerate: false, canAccessBills: false, canAccessAccounts: false,
    canAccessProducts: false, canAccessLogs: true, canAccessProfile: true,
    canAccessTeam: true, canAccessPOS: true,
    canAccessInventory: true,
    canEditInvoice: true, canDeleteInvoice: true,
    canExportPDF: true, canExportExcel: true, canShareInvoice: true,canChangeInvoiceStatus: true,
    canAddProduct: true, canEditProduct: true, canRemoveProduct: true, canImportProducts: true,
    canPOSApplyDiscount: true, canPOSOverridePrice: true, canPOSChangeStatus: true, canPOSOverrideInvNo: true,
    canManageOutlets: false, canManageOutletStaff: false, canViewAllOutlets: false,
    canManageInventory: true, canManageSuppliers: true,
    canManagePayroll: true, canPOSChangeBusinessType: true,canPOSEditDateTime: true, canPOSEditTaxLines: true, canPOSEditShipping: true,
canPOSManageAutoActions: true, canPOSDeleteBill: true, canPOSDiscardBill: true,canEditAccounts: true, canDeleteAccounts: true, canSendPromo: true,
    canAccessKitchenDashboard: true, canAccessPaymentDisplay: true, canPOSToggleBilingual: true,
    canEditTableLayout: true, canManageFloors: true, canAccessTables: true,
    ...PLAN_DEFAULTS,
  },
  admin: {
    canAccessGenerator: false, canAccessInvoiceCreator: false, canAccessBatchGenerate: false, canAccessBills: false, canAccessAccounts: false,
    canAccessProducts: false, canAccessLogs: true, canAccessProfile: false,canChangeInvoiceStatus: true,
    canAccessTeam: true, canAccessPOS: true,
    canAccessInventory: true,
    canEditInvoice: true, canDeleteInvoice: true,
    canExportPDF: true, canExportExcel: true, canShareInvoice: true,
    canAddProduct: true, canEditProduct: true, canRemoveProduct: true, canImportProducts: true,
    canPOSApplyDiscount: true, canPOSOverridePrice: true, canPOSChangeStatus: true, canPOSOverrideInvNo: true,
    canManageOutlets: true, canManageOutletStaff: true, canViewAllOutlets: true,
    canManageInventory: true, canManageSuppliers: true,
    canManagePayroll: true, canPOSChangeBusinessType: true,canPOSEditDateTime: true, canPOSEditTaxLines: true, canPOSEditShipping: true,
canPOSManageAutoActions: true, canPOSDeleteBill: true, canPOSDiscardBill: true,canEditAccounts: true, canDeleteAccounts: true, canSendPromo: true,
    canAccessKitchenDashboard: true, canAccessPaymentDisplay: true, canPOSToggleBilingual: true,
    canEditTableLayout: true, canManageFloors: true, canAccessTables: true,
    ...PLAN_DEFAULTS,
  },
  manager: {
    canAccessGenerator: false, canAccessInvoiceCreator: false, canAccessBatchGenerate: false, canAccessBills: false, canAccessAccounts: false,
    canAccessProducts: false, canAccessLogs: false, canAccessProfile: false,
    canAccessTeam: true, canAccessPOS: true,
    canAccessInventory: true,
    canEditInvoice: true, canDeleteInvoice: false,
    canExportPDF: true, canExportExcel: true, canShareInvoice: true,canChangeInvoiceStatus: true,
    canAddProduct: true, canEditProduct: true, canRemoveProduct: false, canImportProducts: false,
    canPOSApplyDiscount: true, canPOSOverridePrice: false, canPOSChangeStatus: true, canPOSOverrideInvNo: false,
    canManageOutlets: false, canManageOutletStaff: true, canViewAllOutlets: false,
    canManageInventory: true, canManageSuppliers: false,
    canManagePayroll: false, canPOSChangeBusinessType: true,canPOSEditDateTime: true, canPOSEditTaxLines: true, canPOSEditShipping: true,
canPOSManageAutoActions: true, canPOSDeleteBill: true, canPOSDiscardBill: true,canEditAccounts: true, canDeleteAccounts: true, canSendPromo: true,
    canAccessKitchenDashboard: true, canAccessPaymentDisplay: true, canPOSToggleBilingual: true,
    canEditTableLayout: true, canManageFloors: true, canAccessTables: true,
    ...PLAN_DEFAULTS,
  },
  cashier: {
    canAccessGenerator: false, canAccessInvoiceCreator: false, canAccessBatchGenerate: false, canAccessBills: true, canAccessAccounts: false,
    canAccessProducts: false, canAccessLogs: false, canAccessProfile: false,canChangeInvoiceStatus: false,
    canAccessTeam: false, canAccessPOS: true,
    canAccessInventory: false,
    canEditInvoice: false, canDeleteInvoice: false,
    canExportPDF: false, canExportExcel: false, canShareInvoice: true,
    canAddProduct: false, canEditProduct: false, canRemoveProduct: false, canImportProducts: false,
    canPOSApplyDiscount: false, canPOSOverridePrice: false, canPOSChangeStatus: false, canPOSOverrideInvNo: false,
    canManageOutlets: false, canManageOutletStaff: false, canViewAllOutlets: false,
    canManageInventory: false, canManageSuppliers: false,
    canManagePayroll: false, canPOSChangeBusinessType: false, canPOSEditDateTime: false, canPOSEditTaxLines: false, canPOSEditShipping: false,
 canPOSManageAutoActions: false, canPOSDeleteBill: false, canPOSDiscardBill: false,canEditAccounts: false, canDeleteAccounts: false, canSendPromo: false,
    canAccessKitchenDashboard: false, canAccessPaymentDisplay: false, canPOSToggleBilingual: false,
    canEditTableLayout: false, canManageFloors: false, canAccessTables: true,
    ...PLAN_DEFAULTS,
  },
  staff: {
    canAccessGenerator: false, canAccessInvoiceCreator: false, canAccessBatchGenerate: false, canAccessBills: false, canAccessAccounts: false,
    canAccessProducts: false, canAccessLogs: false, canAccessProfile: false,canChangeInvoiceStatus: false,
    canAccessTeam: false, canAccessPOS: true,
    canAccessInventory: true,
    canEditInvoice: true, canDeleteInvoice: false,
    canExportPDF: true, canExportExcel: false, canShareInvoice: true,
    canAddProduct: false, canEditProduct: false, canRemoveProduct: false, canImportProducts: false,
    canPOSApplyDiscount: false, canPOSOverridePrice: false, canPOSChangeStatus: false, canPOSOverrideInvNo: false,
    canManageOutlets: false, canManageOutletStaff: false, canViewAllOutlets: false,
    canManageInventory: false, canManageSuppliers: false,
    canManagePayroll: false, canPOSChangeBusinessType: false, canPOSEditDateTime: false, canPOSEditTaxLines: false, canPOSEditShipping: false,
 canPOSManageAutoActions: false, canPOSDeleteBill: false, canPOSDiscardBill: false,canEditAccounts: false, canDeleteAccounts: false, canSendPromo: false,
    canAccessKitchenDashboard: false, canAccessPaymentDisplay: false, canPOSToggleBilingual: false,
    canEditTableLayout: false, canManageFloors: false, canAccessTables: true,
    ...PLAN_DEFAULTS,
  },
  // ── Module-scoped member roles ─────────────────────────────────────────
  // These roles get NO Full Suite access at all. Their access comes entirely
  // from the module plan they belong to (applied via MODULE_GATES below).
  ledger_member: {
    canAccessGenerator: false, canAccessInvoiceCreator: false, canAccessBatchGenerate: false, canAccessBills: false, canAccessAccounts: false,
    canAccessProducts: false, canAccessLogs: false, canAccessProfile: true,canChangeInvoiceStatus: false,
    canAccessTeam: false, canAccessPOS: false,
    canAccessInventory: false,
    canEditInvoice: false, canDeleteInvoice: false,
    canExportPDF: false, canExportExcel: false, canShareInvoice: false,
    canAddProduct: false, canEditProduct: false, canRemoveProduct: false, canImportProducts: false,
    canPOSApplyDiscount: false, canPOSOverridePrice: false, canPOSChangeStatus: false, canPOSOverrideInvNo: false,
    canManageOutlets: false, canManageOutletStaff: false, canViewAllOutlets: false,
    canManageInventory: false, canManageSuppliers: false,
    canManagePayroll: false, canPOSChangeBusinessType: false, canPOSEditDateTime: false, canPOSEditTaxLines: false, canPOSEditShipping: false,
canPOSManageAutoActions: false, canPOSDeleteBill: false, canPOSDiscardBill: false,canEditAccounts: false, canDeleteAccounts: false, canSendPromo: false,
    canAccessKitchenDashboard: false, canAccessPaymentDisplay: false, canPOSToggleBilingual: false,
    canEditTableLayout: false, canManageFloors: false, canAccessTables: false,
    ...PLAN_DEFAULTS,
  },
  employee_member: {
    canAccessGenerator: false, canAccessInvoiceCreator: false, canAccessBatchGenerate: false, canAccessBills: false, canAccessAccounts: false,
    canAccessProducts: false, canAccessLogs: false, canAccessProfile: true,
    canAccessTeam: false, canAccessPOS: false,canChangeInvoiceStatus: false,
    canAccessInventory: false,
    canEditInvoice: false, canDeleteInvoice: false,
    canExportPDF: false, canExportExcel: false, canShareInvoice: false,
    canAddProduct: false, canEditProduct: false, canRemoveProduct: false, canImportProducts: false,
    canPOSApplyDiscount: false, canPOSOverridePrice: false, canPOSChangeStatus: false, canPOSOverrideInvNo: false,
    canManageOutlets: false, canManageOutletStaff: false, canViewAllOutlets: false,
    canManageInventory: false, canManageSuppliers: false,
    canManagePayroll: false, canPOSChangeBusinessType: false, canPOSEditDateTime: false, canPOSEditTaxLines: false, canPOSEditShipping: false,
canPOSManageAutoActions: false, canPOSDeleteBill: false, canPOSDiscardBill: false,canEditAccounts: false, canDeleteAccounts: false, canSendPromo: false,
    canAccessKitchenDashboard: false, canAccessPaymentDisplay: false, canPOSToggleBilingual: false,
    canEditTableLayout: false, canManageFloors: false, canAccessTables: false,
    ...PLAN_DEFAULTS,
  },
  queue_member: {
    canAccessGenerator: false, canAccessInvoiceCreator: false, canAccessBatchGenerate: false, canAccessBills: false, canAccessAccounts: false,
    canAccessProducts: false, canAccessLogs: false, canAccessProfile: true,canChangeInvoiceStatus: false,
    canAccessTeam: false, canAccessPOS: false,
    canAccessInventory: false,
    canEditInvoice: false, canDeleteInvoice: false,
    canExportPDF: false, canExportExcel: false, canShareInvoice: false,
    canAddProduct: false, canEditProduct: false, canRemoveProduct: false, canImportProducts: false,
    canPOSApplyDiscount: false, canPOSOverridePrice: false, canPOSChangeStatus: false, canPOSOverrideInvNo: false,
    canManageOutlets: false, canManageOutletStaff: false, canViewAllOutlets: false,
    canManageInventory: false, canManageSuppliers: false,
    canManagePayroll: false, canPOSChangeBusinessType: false,canPOSEditDateTime: false, canPOSEditTaxLines: false, canPOSEditShipping: false,
canPOSManageAutoActions: false, canPOSDeleteBill: false, canPOSDiscardBill: false,canEditAccounts: false, canDeleteAccounts: false, canSendPromo: false,
    canAccessKitchenDashboard: false, canAccessPaymentDisplay: false, canPOSToggleBilingual: false,
    canEditTableLayout: false, canManageFloors: false, canAccessTables: false,
    ...PLAN_DEFAULTS,
  },
  gym_member: {
    canAccessGenerator: false, canAccessInvoiceCreator: false, canAccessBatchGenerate: false, canAccessBills: false, canAccessAccounts: false,
    canAccessProducts: false, canAccessLogs: false, canAccessProfile: true,canChangeInvoiceStatus: false,
    canAccessTeam: false, canAccessPOS: false,
    canAccessInventory: false,
    canEditInvoice: false, canDeleteInvoice: false,
    canExportPDF: false, canExportExcel: false, canShareInvoice: false,
    canAddProduct: false, canEditProduct: false, canRemoveProduct: false, canImportProducts: false,
    canPOSApplyDiscount: false, canPOSOverridePrice: false, canPOSChangeStatus: false, canPOSOverrideInvNo: false,
    canManageOutlets: false, canManageOutletStaff: false, canViewAllOutlets: false,
    canManageInventory: false, canManageSuppliers: false,
    canManagePayroll: false,canPOSChangeBusinessType: false, canPOSEditDateTime: false, canPOSEditTaxLines: false, canPOSEditShipping: false,
canPOSManageAutoActions: false, canPOSDeleteBill: false, canPOSDiscardBill: false,canEditAccounts: false, canDeleteAccounts: false, canSendPromo: false,
    canAccessKitchenDashboard: false, canAccessPaymentDisplay: false, canPOSToggleBilingual: false,
    canEditTableLayout: false, canManageFloors: false, canAccessTables: false,
    ...PLAN_DEFAULTS,
  },
  canteen_member: {
    canAccessGenerator: false, canAccessInvoiceCreator: false, canAccessBatchGenerate: false, canAccessBills: false, canAccessAccounts: false,
    canAccessProducts: false, canAccessLogs: false, canAccessProfile: true,canChangeInvoiceStatus: false,
    canAccessTeam: false, canAccessPOS: false,
    canAccessInventory: false,
    canEditInvoice: false, canDeleteInvoice: false,
    canExportPDF: false, canExportExcel: false, canShareInvoice: false,
    canAddProduct: false, canEditProduct: false, canRemoveProduct: false, canImportProducts: false,
    canPOSApplyDiscount: false, canPOSOverridePrice: false, canPOSChangeStatus: false, canPOSOverrideInvNo: false,
    canManageOutlets: false, canManageOutletStaff: false, canViewAllOutlets: false,
    canManageInventory: false, canManageSuppliers: false,
    canManagePayroll: false,canPOSChangeBusinessType: false, canPOSEditDateTime: false, canPOSEditTaxLines: false, canPOSEditShipping: false,
canPOSManageAutoActions: false, canPOSDeleteBill: false, canPOSDiscardBill: false,canEditAccounts: false, canDeleteAccounts: false, canSendPromo: false,
    canAccessKitchenDashboard: false, canAccessPaymentDisplay: false, canPOSToggleBilingual: false,
    canEditTableLayout: false, canManageFloors: false, canAccessTables: false,
    ...PLAN_DEFAULTS,
  },
  pos_member: {
    canAccessGenerator: false, canAccessInvoiceCreator: false, canAccessBatchGenerate: false, canAccessBills: false, canAccessAccounts: false,
    canAccessProducts: false, canAccessLogs: false, canAccessProfile: true, canChangeInvoiceStatus: false,
    canAccessTeam: false, canAccessPOS: false,
    canAccessInventory: false,
    canEditInvoice: false, canDeleteInvoice: false,
    canExportPDF: false, canExportExcel: false, canShareInvoice: false,
    canAddProduct: false, canEditProduct: false, canRemoveProduct: false, canImportProducts: false,
    canPOSApplyDiscount: false, canPOSOverridePrice: false, canPOSChangeStatus: false, canPOSOverrideInvNo: false,
    canManageOutlets: false, canManageOutletStaff: false, canViewAllOutlets: false,
    canManageInventory: false, canManageSuppliers: false,
    canManagePayroll: false, canPOSChangeBusinessType: false, canPOSEditDateTime: false, canPOSEditTaxLines: false, canPOSEditShipping: false,
    canPOSManageAutoActions: false, canPOSDeleteBill: false, canPOSDiscardBill: false, canEditAccounts: false, canDeleteAccounts: false, canSendPromo: false,
    canAccessKitchenDashboard: true, canAccessPaymentDisplay: true, canPOSToggleBilingual: true,
    canEditTableLayout: true, canManageFloors: true, canAccessTables: true,
    ...PLAN_DEFAULTS,
  },
  viewer: {
    canAccessGenerator: false, canAccessInvoiceCreator: false, canAccessBatchGenerate: false, canAccessBills: true, canAccessAccounts: false,
    canAccessProducts: false, canAccessLogs: false, canAccessProfile: false,canChangeInvoiceStatus: false,
    canAccessTeam: false, canAccessPOS: false,
    canAccessInventory: false,
    canEditInvoice: false, canDeleteInvoice: false,
    canExportPDF: false, canExportExcel: false, canShareInvoice: true,
    canAddProduct: false, canEditProduct: false, canRemoveProduct: false, canImportProducts: false,
    canPOSApplyDiscount: false, canPOSOverridePrice: false, canPOSChangeStatus: false, canPOSOverrideInvNo: false,
    canManageOutlets: false, canManageOutletStaff: false, canViewAllOutlets: false,
    canManageInventory: false, canManageSuppliers: false,
    canManagePayroll: false, canPOSChangeBusinessType: false, canPOSEditDateTime: false, canPOSEditTaxLines: false, canPOSEditShipping: false,
canPOSManageAutoActions: false, canPOSDeleteBill: false, canPOSDiscardBill: false,canEditAccounts: false, canDeleteAccounts: false, canSendPromo: false,
    canAccessKitchenDashboard: false, canAccessPaymentDisplay: false, canPOSToggleBilingual: false,
    canEditTableLayout: false, canManageFloors: false, canAccessTables: false,
    ...PLAN_DEFAULTS,
  },
};

// ── Independent module plans ────────────────────────────────────────────────
// Each of these is its OWN product, sold and billed separately from the Full
// Suite plan (₹400/mo flat, 15-day free trial). A user can hold any combination
// of these regardless of what Full Suite plan (or no Full Suite plan) they have.
//
// Status lifecycle per module, per account:
//   'inactive'     → never subscribed / trialed. No access at all.
//   'trial'        → inside the 15-day window. Full access.
//   'active'       → paid and current. Full access.
//   'locked'       → trial ended, never paid (or payment lapsed). VIEW-ONLY:
//                    they can see their existing data but can't create, edit,
//                    delete, or send anything until they pay.

export type ModuleId = 'ledger' | 'employees' | 'queue' | 'gym' | 'canteen' | 'pos';
export type ModuleStatus = 'inactive' | 'trial' | 'active' | 'locked';

// Full access — granted for 'trial' and 'active'
const MODULE_FULL_GATES: Record<ModuleId, Partial<Permissions>> = {
  ledger: {
    canAccessLedger: true,
    canAccessTeam: true,
    canAccessAccounts: true,
    canLedgerCreateTab: true,
    canLedgerAddCharge: true,
    canLedgerAddPayment: true,
    canLedgerSettle: true,
    canLedgerDeleteEntry: true,
    canLedgerBroadcast: true,
    canLedgerSendWA: true,
    canLedgerExportExcel: true,
    canLedgerShareQR: true,
    canLedgerQuickMsg: true,
    canLedgerNotes: true,
    canLedgerRecurring: true,
    canLedgerPayLink: true,
    canLedgerEditTab: true,
    canLedgerDeleteTab: true,
    canLedgerViewStatus: true,
    canLedgerManageCategories: true,
  },
  employees: {
    canAccessEmployees: true,
    canAccessAccounts: true,
    canAccessTeam: true,
    canManageEmployees: true,
    canManageLeaves: true,
    canManagePayroll: true,
    canRequestLeave: true,
    canManageAttendance: true,
    canViewAttendance: true,
    canViewLeaves: true,
    canViewPayroll: true,
  },
  queue: {
    canAccessQueue: true,
    canAccessClinic: true,
    canAccessAccounts: true,
    canAccessTeam: true,
    canQueueIssueToken: true,
    canQueueCallNext: true,
    canQueueUpdateStatus: true,
    canQueueSendWhatsApp: true,
    canQueueSendEmail: true,
    canQueueEditToken: true,
    canQueueDeleteToken: true,
    canQueueManageCounters: true,
    canQueueSettings: true,
    canQueueViewLogs: true,
  },
  gym: {
    canAccessFitness: true,
    canAccessAccounts: true,
    canAccessTeam: true,
    canGymCheckInQR: true,
    canGymCheckInFace: true,
    canGymCheckInFinger: true,
    canGymAddMember: true,
    canGymEditMember: true,
    canGymDeleteMember: true,
    canGymViewBadge: true,
    canGymRegisterFace: true,
    canGymVerifyOtp: true,
    canGymSendReports: true,
    canGymSendPaymentLink: true,
    canGymViewStatus: true,
    canGymManagePlans: true,
    canGymManageKiosk: true,
  },
  canteen: {
    canAccessFood: true,
    canAccessAccounts: true,
    canAccessTeam: true,
    canFoodCheckInQR: true,
    canFoodCheckInFace: true,
    canFoodCheckInFinger: true,
    canFoodAddMember: true,
    canFoodEditMember: true,
    canFoodDeleteMember: true,
    canFoodViewBadge: true,
    canFoodRegisterFace: true,
    canFoodVerifyOtp: true,
    canFoodSendReports: true,
    canFoodSendPaymentLink: true,
    canFoodViewStatus: true,
    canFoodManagePlans: true,
    canFoodManageKiosk: true,
  },
  pos: {
    canAccessPOS: true,
    canAccessProducts: true,
    canAccessInventory: true,
    canAccessGenerator: true,
    canAccessInvoiceCreator: true,
    canAccessBatchGenerate: true,
    canAccessTeam: true,
    canAccessAccounts: true,
    canAccessBills: true,
    canAccessKitchenDashboard: true,
    canAccessPaymentDisplay: true,
    canPOSToggleBilingual: true,
    // Added so the final safety clamp below actually gates these — without
    // appearing in MODULE_FULL_GATES, a key never enters moduleGatedKeys and
    // the clamp skips it entirely, so ROLE_PERMISSIONS's hardcoded
    // `canAccessTables: true` (owner/individual/admin/manager/cashier/staff/
    // pos_member all set this directly) would survive untouched even with
    // pos inactive/locked. Deliberately left OUT of MODULE_LOCKED_GATES.pos
    // below — mirrors the backend's MODULE_LOCKED_ALLOWED_KEYS["pos"] = set()
    // decision: locked pos means table management is fully gated, no
    // view-only carve-out, same all-or-nothing rule as canAccessPOS itself.
    canAccessTables: true,
    canEditTableLayout: true,
    canManageFloors: true,
    // POS active should unlock the same invoicing-form features Pro/Ultra
    // grants — these were previously ONLY in PLAN_GATES, so an active POS
    // module never touched them and Tax Lines/Discount Lines/Custom
    // Columns/Bilingual stayed locked on the basic plan even with POS paid.
    canAddMultipleTaxLines: true,
    canAddDiscountLines: true,
    canUseCustomColumns: true,
    canUseBilingual: true,
    canUseShipping: true,
    canUseNotes: true,
    canUseScanner: true,
  },
};

// Locked (trial expired, unpaid) — view access stays true so they can see their
// own data and get the "pay to unlock" prompt; every write/action goes false.
const MODULE_LOCKED_GATES: Record<ModuleId, Partial<Permissions>> = {
  ledger: {
    canAccessLedger: true,
    canLedgerCreateTab: false,
    canLedgerAddCharge: false,
    canLedgerAddPayment: false,
    canLedgerSettle: false,
    canLedgerDeleteEntry: false,
    canLedgerBroadcast: false,
    canLedgerSendWA: false,
    canLedgerExportExcel: false,
    canLedgerShareQR: false,
    canLedgerQuickMsg: false,
    canLedgerNotes: false,
    canLedgerRecurring: false,
    canLedgerPayLink: false,
    canLedgerEditTab: false,
    canLedgerDeleteTab: false,
    canLedgerViewStatus: true,
    canLedgerManageCategories: false,
  },
  employees: {
    canAccessEmployees: true,
    canAccessAccounts: true,
    canManageEmployees: false,
    canManageLeaves: false,
    canManagePayroll: false,
    canRequestLeave: false,
    canManageAttendance: false,
    canViewAttendance: true,
    canViewLeaves: true,
    canViewPayroll: true,
  },
  queue: {
    canAccessQueue: true,
    canAccessClinic: true,
    canAccessAccounts: true,
    canQueueIssueToken: false,
    canQueueCallNext: false,
    canQueueUpdateStatus: false,
    canQueueSendWhatsApp: false,
    canQueueSendEmail: false,
    canQueueEditToken: false,
    canQueueDeleteToken: false,
    canQueueManageCounters: false,
    canQueueSettings: false,
    canQueueViewLogs: true,
  },
  gym: {
    canAccessFitness: true,
    canAccessAccounts: true,
    canGymCheckInQR: false,
    canGymCheckInFace: false,
    canGymCheckInFinger: false,
    canGymAddMember: false,
    canGymEditMember: false,
    canGymDeleteMember: false,
    canGymViewBadge: true,
    canGymRegisterFace: false,
    canGymVerifyOtp: false,
    canGymSendReports: false,
    canGymSendPaymentLink: false,
    canGymViewStatus: true,
    canGymManagePlans: false,
    canGymManageKiosk: false,
  },
  canteen: {
    canAccessFood: true,
    canAccessAccounts: true,
    canFoodCheckInQR: false,
    canFoodCheckInFace: false,
    canFoodCheckInFinger: false,
    canFoodAddMember: false,
    canFoodEditMember: false,
    canFoodDeleteMember: false,
    canFoodViewBadge: true,
    canFoodRegisterFace: false,
    canFoodVerifyOtp: false,
    canFoodSendReports: false,
    canFoodSendPaymentLink: false,
    canFoodViewStatus: true,
    canFoodManagePlans: false,
    canFoodManageKiosk: false,
  },
  pos: {
    canAccessPOS: false,
    canAccessTables: false,
    canEditTableLayout: false,
    canManageFloors: false,
    canAccessInventory: false,
    canAccessKitchenDashboard: false,
    canAccessPaymentDisplay: false,
    canPOSToggleBilingual: false,
  },
};

// Module limits — flat now, no tiers. Checked on backend, exposed here for UI display.
export const MODULE_LIMITS: Record<ModuleId, Record<string, number | string>> = {
  ledger:    { ledger_tabs: 999999,  broadcast: true, seats: 999999, outlets: 999999, price_inr: 400 },
  employees: { employees: 999999,    payroll: true,   seats: 999999, outlets: 999999, price_inr: 400 },
  queue:     { counters: 999999,     display_screen: true, seats: 999999, outlets: 999999, price_inr: 400 },
  gym:       { members: 999999,      seats: 999999,   outlets: 999999, price_inr: 400 },
  canteen:   { members: 999999,      seats: 999999,   outlets: 999999, price_inr: 400 },
};

export function getPermissions(
  role?: string | null,
  dbPerms?: Record<string, boolean> | null,
  plan?: string | null,
  planTier?: string | null,
  planStatus?: string | null,
  moduleStatuses?: Partial<Record<ModuleId, ModuleStatus>> | null,
  hadTrial?: boolean | null,
): Permissions {
  // Only the account that actually holds the plan (owner) or a solo/individual
  // account should have its nav access driven directly by the plan. Every other
  // team-member role (admin, manager, cashier, staff, viewer, module roles)
  // must be governed by their role defaults + whatever the owner explicitly
  // toggled for them in IAM — the plan should never silently override that.
  const isOwnerLevelRole = role === 'owner' || role === 'individual';

  // Get role base
  let base: Permissions;
  if (role === 'owner') base = { ...ROLE_PERMISSIONS['owner'] };
  else if (role === 'individual') base = { ...ROLE_PERMISSIONS['individual'] };
  else base = { ...(ROLE_PERMISSIONS[(role as Role)] ?? ROLE_PERMISSIONS.viewer) };



  // Apply Full Suite plan gates — these now only govern invoicing-side features
  // (POS, bilingual, WhatsApp, inventory, etc). They no longer touch Ledger /
  // Employees / Queue / Clinic / Fitness / Food — those come from module plans.
  const planKey = (plan || 'basic') as PlanId;
  const tier = (planTier || 'individual').toLowerCase();
  const gates = { ...(PLAN_GATES[planKey] ?? PLAN_GATES.basic) };

  if (tier === 'individual') {
    gates.canAccessPOS       = false;
    gates.canAccessInventory = false;
  }

  // Keys legitimately granted via the Suite plan itself (not a module) —
  // the final safety clamp below must not strip these just because no
  // module happens to be active/trial/locked.
  const planGrantedKeys = new Set<string>();

  if (isOwnerLevelRole) {
    for (const [key, val] of Object.entries(gates)) {
      (base as any)[key] = val;
      if (val) planGrantedKeys.add(key);
    }
  }
  
  // Expired/cancelled Full Suite — lock paid invoicing features
  if (
    planStatus === 'expired' ||
    planStatus === 'cancelled'
  ) {
    base.canAccessPOS        = false;
    base.canAccessInventory  = false;
    base.canUseBilingual     = false;
    base.canUseWhatsApp      = false;
    base.canUseEmailReminder = false;
    base.canMergePDF         = false;
    base.canUseDriveSync     = false;
    base.canExportExcelBulk  = false;
    base.canUseSmartReconciliation = false;
    base.canUseFaceRecognition     = false;
    base.canUseFingerprint   = false;
    base.canUseScanner       = false;
  }

  // Apply independent module plan gates. Special rule: if ANY module is in
  // 'trial' status, that single trial unlocks ALL modules for the duration —
  // not just the one that was clicked. Paid ('active') modules stay scoped
  // to themselves as normal; only the trial state is shared.
  let hasActivePaidModule = false;
  const anyModuleInTrial = moduleStatuses
    ? Object.values(moduleStatuses).some(s => s === 'trial')
    : false;

  // Module trial OR suite trial = full access, no tier restrictions
  // Only applies to the account that actually holds the plan/trial — team
  // members must keep their assigned role/permissions during a trial too.
  if ((anyModuleInTrial || planStatus === 'trial') && isOwnerLevelRole) {
    const trialGates = { ...PLAN_GATES['pro'] };
    for (const [key, val] of Object.entries(trialGates)) { (base as any)[key] = val; if (val) planGrantedKeys.add(key); }
    base.canAccessGenerator = true; planGrantedKeys.add('canAccessGenerator');
    base.canAccessBills     = true; planGrantedKeys.add('canAccessBills');
    base.canAccessAccounts  = true; planGrantedKeys.add('canAccessAccounts');
    base.canAccessProducts  = true; planGrantedKeys.add('canAccessProducts');
    base.canAccessPOS       = true; planGrantedKeys.add('canAccessPOS');
    base.canAccessInventory = true; planGrantedKeys.add('canAccessInventory');
    base.canAccessTeam      = true; planGrantedKeys.add('canAccessTeam');
     // Trial = every module unlocked too, no exceptions
    for (const gate of Object.values(MODULE_FULL_GATES)) {
      for (const [key, val] of Object.entries(gate)) (base as any)[key] = val;
    }
  }
const MODULE_MEMBER_ROLE: Record<ModuleId, Role> = {
  ledger: 'ledger_member', employees: 'employee_member',
  queue: 'queue_member', gym: 'gym_member', canteen: 'canteen_member',pos: 'pos_member',
 };
  if (moduleStatuses) {
    for (const moduleId of Object.keys(MODULE_FULL_GATES) as ModuleId[]) {
      const status = moduleStatuses[moduleId];
    const roleQualifies = isOwnerLevelRole || role === MODULE_MEMBER_ROLE[moduleId];
     if (!roleQualifies) continue;
      if (status === 'active') {
        const fullGate = MODULE_FULL_GATES[moduleId];
        if (fullGate) for (const [key, val] of Object.entries(fullGate)) (base as any)[key] = val;
        if (moduleId !== 'pos') hasActivePaidModule = true;
      } else if (anyModuleInTrial) {
        // Shared trial — every module gets full access regardless of its own status
        const fullGate = MODULE_FULL_GATES[moduleId];
        if (fullGate) for (const [key, val] of Object.entries(fullGate)) (base as any)[key] = val;
      } else if (status === 'locked') {
        const lockedGate = MODULE_LOCKED_GATES[moduleId];
        if (lockedGate) {
          for (const [key, val] of Object.entries(lockedGate)) {
            if (planGrantedKeys.has(key)) continue;
            (base as any)[key] = val;
          }
        }
      }
      // status undefined/'inactive' and no shared trial active → leave as PLAN_DEFAULTS (all false)
    }
  }
   // Fraud-prevention toggles: Currency, WhatsApp/Gmail, and Payment Gateways
  // control where messages and money go. These default to owner-only
  // (and individual accounts, which are solo owners) — NOT tied to
  // canManageOutlets or any role default — so an admin/manager only gets
  // access if the owner explicitly flips the toggle on in IAM.
  base.canManageProfileMessaging = isOwnerLevelRole;
  base.canManageCurrency = isOwnerLevelRole;
  base.canManageGateways = isOwnerLevelRole;

   // Apply db permission overrides — must run AFTER module gates, so a
  // specific user's IAM toggle (e.g. turning off canGymAddMember) can
  // actually override the module's blanket full-access grant.
  if (dbPerms) {
    for (const [key, val] of Object.entries(dbPerms)) {
      if (key in base) (base as any)[key] = val;
    }
  }
  // Policy: basic invoicing (Invoice Creator, Quick Invoice, Batch Generate,
  // Bills, Accounts) is free for every owner-level account regardless of
  // plan or module — it was already granted above via PLAN_GATES.basic and
  // must never be stripped just because the account also has a module.
  // Modules only ADD capability (POS, Ledger, etc.) on top of that baseline;
  // they never replace or remove it. (Previously this block zeroed out
  // invoicing for module-only accounts — that's now removed.)

  // ── Final safety clamp ──────────────────────────────────────────────────
  // A permission that belongs to a specific module (Gym, Ledger, Queue, etc.)
  // must never end up "on" unless the ORG actually has that module active,
  // in trial, or locked (locked keeps its small view-only set) — no matter
  // what role someone has, what a dbPerms override says, or whether "Grant
  // All" was clicked. Checked last, after every other rule above, so a
  // toggle that isn't backed by a real module subscription can show as
  // flipped in the IAM panel but will never actually take effect.
  // This runs for every role, including owner/admin — not just module-seat
  // roles — since a permission with no plan behind it is meaningless
  // regardless of who holds it.
  const moduleGatedKeys = new Set<string>();
  for (const gate of Object.values(MODULE_FULL_GATES)) {
    for (const key of Object.keys(gate)) moduleGatedKeys.add(key);
  }

  const allowedTrue = new Set<string>();
  for (const moduleId of Object.keys(MODULE_FULL_GATES) as ModuleId[]) {
    const status = moduleStatuses?.[moduleId];
    if (status === 'active' || anyModuleInTrial || planStatus === 'trial') {
      for (const [key, val] of Object.entries(MODULE_FULL_GATES[moduleId])) {
        if (val) allowedTrue.add(key);
      }
    } else if (status === 'locked') {
      for (const [key, val] of Object.entries(MODULE_LOCKED_GATES[moduleId])) {
        if (val) allowedTrue.add(key);
      }
    }
    // status undefined/'inactive' → contributes nothing to allowedTrue
  }

  for (const key of moduleGatedKeys) {
    if (planGrantedKeys.has(key)) continue; // granted by the Suite plan itself, not a module
    if (!allowedTrue.has(key) && (base as any)[key] === true) {
      (base as any)[key] = false;
    }
  }

  return base;
}